<template>
    <div class="general-attribute-picker">
        <div
            class="d-flex flex-column justify-content-center text-center align-items-center mb-5"
            v-if="!filterAttributes().length"
        >
            <div>
                <span class="material-icons"> search </span>
            </div>
            <div>{{ $t("chatbotv2.noResultFound") }}</div>
        </div>
        <div class="attr-select" v-for="attributeGroup of getAttributeGroups()">
            <div text-color="lighter" text-weight="semi-bold" text-type="h5" class="tpl-category-select-category-name">
                {{ attributeGroup.label }}
            </div>
            <div
                data-checked="true"
                data-index="0"
                data-item-label="Name"
                class="picker-item"
                v-for="attr of filterAttributes(attributeGroup.type)"
                @click="pick(attr.value)"
                :class="{ active: defaultSelected === attr.name }"
            >
                <template v-if="attributeGroup.icon === 'material-icons'">
                    <span class="material-icons"> {{ attributeGroup.iconValue }} </span>
                </template>
                <template v-else-if="attributeGroup.icon === 'component'">
                    <AttributeIconPreview :attribute="attr.value" />
                </template>
                <span class="name">
                    {{ isLocalizedAttribute(attr.name) ? $t(`chatbotv2.attr.${attr.name}`) : attr.name }}
                </span>
                <span class="description"> {{ attr.placeholder }} </span>
            </div>
        </div>
        <hr />
        <AddCustomAttribute :on-added="pick" />
    </div>
</template>

<script>
import AddCustomAttribute from "../common/AddCustomAttribute";
import AttributeIconPreview from "../common/AttributeIconPreview";
import systemAttributes from "../../../utils/systemAttributes";
import { mapGetters, mapState } from "vuex";
import _ from "lodash";
import log2 from "../../../../../../utils/log2";
import CONFIG from "../../../../../../config/config";
import { Attributes, Entities } from "../../../palatteEnums";
import { CHATBOT_API } from "../../../../../../api/chatbotv2";
import flatDiagram from "../../../utils/flatDiagram";

const log = log2("GeneralAttributePicker");
export default {
    name: "GeneralAttributePicker",
    components: {
        AddCustomAttribute,
        AttributeIconPreview,
        systemAttributes,
    },
    props: {
        storyId: {
            type: String,
            required: true,
        },
        defaultSelected: {
            type: String,
            required: false,
        },
        pick: {
            type: Function,
            required: true,
        },
        filterMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        searchTextProp: {
            type: String,
            require: false,
        }
    },
    data() {
        return {
            defaultAttributes: systemAttributes,
            Attributes,
            Entities,
            aiFunctions: [],
            selectedParentAttribute: null,
            searchText: this.searchTextProp,
        };
    },
    computed: {
        ...mapGetters({
            board: "CB/board",
        }),
        ...mapState({
            visitorContactAttributes: (state) => state.contactProperty.data || [],
        }),
    },
    watch: {
        searchTextProp(newVal) {
            this.searchText = newVal;
        },
    },
    methods: {
        getAttributeGroups() {
            const board = this.board(this.storyId);
            const flatted = flatDiagram(board.diagram);

            const isExistsAkinonBlock = flatted.some((block) => block.type.startsWith("AKINON_"));
            const isExistsIdeaSoft = flatted.some((block) => block.type.startsWith("IDEASOFT_"));
            const isExistsTsoft = flatted.some((block) => block.type.startsWith("TSOFT_"));
            const isExistsProbel = flatted.some((block) => block.type.startsWith("PROBEL_"));
            const isExistsB2Cargo = flatted.some((block) => block.type.startsWith("B2CARGO_"));
            const isExistsHolidayPlus = flatted.some((block) => block.type.startsWith("HOLIDAY_PLUS_"));
            const isExistsIkas = flatted.some((block) => block.type.startsWith("IKAS_"));
            const isExistsTicimaxV2 = flatted.some((block) => block.type.startsWith("TICIMAX_V2_"));

            return [
                {
                    isShowing: this.filterAttributes("custom").length > 0,
                    type: "custom",
                    label: this.$t("chatbotv2.CUSTOM_ATTRIBUTES"),
                    icon: "material-icons",
                    iconValue: "data_object",
                },
                {
                    isShowing: this.filterAttributes("system").length > 0,
                    type: "system",
                    label: this.$t("chatbotv2.USER_DATA"),
                    icon: "component",
                },
                {
                    isShowing: this.filterAttributes("visitorContactProperty").length > 0,
                    type: "visitorContactProperty",
                    label: "VISITOR CONTACT PROPERTIES",
                    icon: "component",
                },
                {
                    isShowing: this.aiFunctions.length > 0 && this.filterAttributes("ai-function").length > 0,
                    type: "ai-function",
                    label: "AI FUNCTION",
                    icon: "component",
                },
                {
                    isShowing: isExistsAkinonBlock && this.filterAttributes("akinon").length > 0,
                    type: "akinon",
                    label: "AKINON ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: isExistsIdeaSoft && this.filterAttributes("ideasoft").length > 0,
                    type: "ideasoft",
                    label: "IDEASOFT ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: isExistsTsoft && this.filterAttributes("tsoft").length > 0,
                    type: "tsoft",
                    label: "TSOFT ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: isExistsTicimaxV2 && this.filterAttributes("ticimax").length > 0,
                    type: "ticimax",
                    label: "TICIMAX ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: isExistsIkas && this.filterAttributes("ikas").length > 0,
                    type: "ikas",
                    label: "IKAS ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: isExistsProbel && this.filterAttributes("probel").length > 0,
                    type: "probel",
                    label: "PROBEL ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: isExistsB2Cargo && this.filterAttributes("b2cargo").length > 0,
                    type: "b2cargo",
                    label: "B2CARGO ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: isExistsHolidayPlus && this.filterAttributes("holiday_plus").length > 0,
                    type: "holiday_plus",
                    label: "HOLIDAY+ ATTRIBUTES",
                    icon: "component",
                },
                {
                    isShowing: this.filterAttributes("trendyol").length > 0,
                    type: "trendyol",
                    label: "TRENDYOL ATTRIBUTES",
                    icon: "component",
                },
            ].filter((group) => group.isShowing);
        },
        getAIFunctionAttributes() {
            const items = this.aiFunctions.map((x) => x.aiFunction);

            const attrs = items
                .map((fn) => {
                    return (fn.parameters || []).map((param) => {
                        const { name, type, description } = param;
                        const enumValues = param["enum"];
                        const key = `ai_function_parameter_${fn.name}___${name}`;
                        return {
                            name: `Function(${fn.name}) - Parameter(${name})`,
                            placeholder: "",
                            value: key,
                            type: "ai-function",
                            // always: false,
                            // filterMode: true,
                            special: true,
                            defaultOperator: "equal",
                            defaultFilterValue: Array.isArray(enumValues) && enumValues.length ? enumValues[0] : "",
                            controlType: Array.isArray(enumValues) && enumValues.length ? "select" : undefined,
                            options: enumValues.map((x) => ({ label: x, value: x })),
                        };
                    });
                })
                .flat();
            const result = [
                {
                    name: "YZ Fonksiyon",
                    placeholder: "Sipariş Oluştur",
                    value: "ai_function_name",
                    type: "ai-function",
                    defaultOperator: "equal",
                    defaultFilterValue: items[0]?.name || "",
                    // always: false,
                    // filterMode: true,
                    special: true,
                    controlType: "select",
                    options: items.map((x) => ({ label: x.name, value: x.name })),
                },
                ...attrs,
            ];
            console.log("getAIFunctionAttributes", { result });
            return result;
        },
        fetchAIFunctions() {
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(this.storyId, "aiFunction", 1, 100).then((res) => {
                this.aiFunctions = res.data.rows;
            });
        },
        isLocalizedAttribute(attribute) {
            const isAkinon = attribute.startsWith("akinon");
            const isIdeasoft = attribute.startsWith("ideasoft");
            const isTsoft = attribute.startsWith("tsoft");
            const isTicimax = attribute.startsWith("ticimax");
            const isProbel = attribute.startsWith("probel");
            const isB2Cargo = attribute.startsWith("b2cargo");
            const isHolidayPlus = attribute.startsWith("holiday_plus");
            const isTrendyol = attribute.startsWith("trendyol");
            const isIkas = attribute.startsWith("ikas");
            const isEntity = Entities.includes(attribute);
            const isSystemAttributes = systemAttributes.find((attr) => attr.value === attribute);
            return (
                isB2Cargo ||
                isHolidayPlus ||
                isAkinon ||
                isProbel ||
                isEntity ||
                isSystemAttributes ||
                isIdeasoft ||
                isTrendyol ||
                isTsoft ||
                isTicimax ||
                isIkas
            );
        },
        filterAttributes(type) {
            const currentBoard = this.board(this.storyId);
            log("currentBoard", currentBoard);
            const aiFunctionAttributes = this.getAIFunctionAttributes();
            const specialAttributeNames = [
                ...CONFIG.AKINON.attributes.map((x) => x.value),
                ...CONFIG.PROBEL.attributes.map((x) => x.value),
                ...CONFIG.IDEASOFT.attributes.map((x) => x.value),
                ...CONFIG.B2CARGO.attributes.map((x) => x.value),
                ...CONFIG.HOLIDAY_PLUS.attributes.map((x) => x.value),
                ...CONFIG.TRENDYOL.attributes.map((x) => x.value),
                ...CONFIG.TSOFT.attributes.map((x) => x.value),
                ...CONFIG.IKAS.attributes.map((x) => x.value),
                ...CONFIG.TICIMAX.attributes.map((x) => x.value),
            ];
            let allAttributes = [
                ...(this.filterMode
                    ? [
                          {
                              name: "Message",
                              placeholder: "How are you?",
                              value: "message",
                              type: "system",
                          },
                      ]
                    : []),
                ...this.defaultAttributes.filter((x) => (this.filterMode ? true : !x.filterMode || x.always)),
                ...this.visitorContactAttributes.map((x) => ({
                    name: x.label,
                    placeholder: "",
                    value: "contact_property_" + x._id,
                    type: "visitorContactProperty",
                    availableOperators: ["equal", "not_equal", "empty", "non_empty"],
                    defaultFilterValue: "",
                })),
                ..._.get(currentBoard, "state.attributes", []).map((x) => ({
                    name: x,
                    placeholder: "",
                    value: x,
                    type: "custom",
                })),
                ...aiFunctionAttributes,
            ];
            allAttributes = allAttributes.filter((x) => !specialAttributeNames.includes(x.value));

            if (CONFIG.AKINON.enabled) {
                allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.AKINON.attributes);
            }
            if (CONFIG.PROBEL.enabled) {
                allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.PROBEL.attributes);
            }
            if (CONFIG.B2CARGO.enabled) {
                allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.B2CARGO.attributes);
            }
            if (CONFIG.HOLIDAY_PLUS.enabled) {
                allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.HOLIDAY_PLUS.attributes);
            }
            if (CONFIG.TRENDYOL.enabled) {
                allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.TRENDYOL.attributes);
            }

            allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.IDEASOFT.attributes);
            allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.TSOFT.attributes);
            allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.IKAS.attributes);
            allAttributes = _.concat(_.uniqBy(allAttributes, "value"), CONFIG.TICIMAX.attributes);

            let result = !!this.searchText
                ? allAttributes.filter((x) => this.searchText === "" || new RegExp(this.searchText, "gi").test(x.name))
                : allAttributes;

            result = result.filter((x) => {
                return _.isEmpty(type) || x.type === type;
            });
            if (type === "custom") {
                const customAttributesPrefixList = ["akinon", "probel", "ideasoft", "b2cargo", "tsoft", "ikas", "ticimax"];
                result = result.filter((x) => !customAttributesPrefixList.some((prefix) => x.name.startsWith(prefix)));
            }
            return result;
        },
    },
    created() {
        this.fetchAIFunctions();
    },
};
</script>

<style lang="scss">
.general-attribute-picker {
    flex-grow: 1;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 15px 15px;
    margin: 0px 5px 0px 5px;
    gap: 10px;

    .attr-select {
        margin-bottom: 10px;
    }

    .picker-item {
        -webkit-box-align: center;
        -ms-flex-align: center;
        align-items: center;
        cursor: pointer;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        height: 32px;
        padding: 0 5px;
        -webkit-transition: background-color 0.2s, opacity 0.2s;
        transition: background-color 0.2s, opacity 0.2s;

        &.active {
            color: #006cff !important;

            .material-icons,
            .name,
            .description {
                color: #006cff !important;
            }
        }

        &:hover {
            background-color: #f0f4f7;
        }

        .material-icons,
        .custom-attr-logo {
            color: #647283;
            font-size: 25px;
            margin-right: 11px;
        }

        .name {
            color: #22272d;
            font-size: 16px;
            font-weight: 400;
            line-height: 20px;
            margin-right: 30px;
        }

        .description {
            color: #9ba6b3;
            font-size: 15px;
            line-height: 19px;
            margin-left: auto;
            overflow: hidden !important;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}
</style>
